import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, updateDoc, doc, setDoc } from 'firebase/firestore';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import app from '../firebase';

const C = {
  bg:'#0D1B2A', bgCard:'#13223A', bgDeep:'#0A1520',
  border:'rgba(255,255,255,0.06)', borderMid:'rgba(255,255,255,0.10)',
  orange:'#FF6B00', orangeDim:'rgba(255,107,0,0.15)', orangeBorder:'rgba(255,107,0,0.35)',
  textPrimary:'#FFFFFF', textSecondary:'#8FA3C0', textMuted:'#5A7A9A',
  green:'#00C851', greenDim:'rgba(0,200,81,0.12)',
};
const font = `'SF Pro Display',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif`;

// Your VAPID public key — get this from:
// Firebase Console → Project Settings → Cloud Messaging → Web Push certificates → Key pair
// It looks like: BH5y3...long string...
const VAPID_KEY = 'BOP7pVv0i2WLoy3hgq_nKkoMF1cYlZruNjsETJnYNjL0qUq3qctC_ovY6mTA4wKqghfbdhdMqDAiPpjZJM78lXs';

// ─── FCM SETUP ────────────────────────────────────────────────────────────────
// Call once when user opens the notifications panel.
// Requests permission, gets the FCM token, and saves it to userProfiles.
async function setupPushNotifications(userId) {
  try {
    // Only supported in browsers that have service worker support
    if (!('serviceWorker' in navigator) || !('Notification' in window)) return null;

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;

    const messaging = getMessaging(app);

    // Get FCM token — this requires the firebase-messaging-sw.js in public/
    const token = await getToken(messaging, {
      vapidKey:              VAPID_KEY,
      serviceWorkerRegistration: await navigator.serviceWorker.ready,
    });

    if (!token) return null;

    // Save token to userProfiles so the Cloud Function can look it up
    await setDoc(doc(db, 'userProfiles', userId), {
      fcmToken:          token,
      fcmTokenUpdatedAt: new Date().toISOString(),
      pushEnabled:       true,
    }, { merge: true });

    return token;
  } catch (err) {
    console.error('FCM setup error:', err);
    return null;
  }
}

export default function Notifications({ user, onClose }) {
  const [notifications,  setNotifications]  = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [pushStatus,     setPushStatus]     = useState('idle'); // 'idle'|'requesting'|'granted'|'denied'
  const [showPushBanner, setShowPushBanner] = useState(false);

  useEffect(() => {
    loadNotifications();
    checkPushStatus();
    // If permission already granted, ensure token is saved
    if (Notification.permission === 'granted') {
      setupPushNotifications(user.uid);
    }


    // Listen for foreground FCM messages while this panel is open
    if ('serviceWorker' in navigator) {
      try {
        const messaging = getMessaging(app);
        const unsub = onMessage(messaging, (payload) => {
          // Foreground message — add it to the list in real time
          const newNotif = {
            id:        `fcm_${Date.now()}`,
            type:      payload.data?.type || 'streak_reminder',
            title:     payload.notification?.title || '',
            message:   payload.notification?.body  || '',
            read:      false,
            createdAt: { seconds: Math.floor(Date.now() / 1000), toDate: () => new Date() },
          };
          setNotifications(prev => [newNotif, ...prev]);
        });
        return () => unsub();
      } catch (err) {
        // Messaging not supported (e.g. Firefox private mode) — silently skip
      }
    }
  }, []);

  const checkPushStatus = async () => {
    if (!('Notification' in window)) return;
    const perm = Notification.permission;
    if (perm === 'granted') {
      setPushStatus('granted');
    } else if (perm === 'denied') {
      setPushStatus('denied');
    } else {
      // 'default' — not asked yet, show the banner
      setShowPushBanner(true);
    }
  };

  const handleEnablePush = async () => {
    setPushStatus('requesting');
    setShowPushBanner(false);
    const token = await setupPushNotifications(user.uid);
    if (token) {
      setPushStatus('granted');
    } else {
      const perm = Notification.permission;
      setPushStatus(perm === 'denied' ? 'denied' : 'idle');
      if (perm !== 'denied') setShowPushBanner(true);
    }
  };

  const loadNotifications = async () => {
    try {
      const q    = query(collection(db,'notifications'), where('userId','==',user.uid));
      const snap = await getDocs(q);
      const all  = snap.docs.map(d => ({ id:d.id, ...d.data() }))
        .sort((a,b) => (b.createdAt?.seconds||0) - (a.createdAt?.seconds||0));
      setNotifications(all);

      // Auto mark all as read when panel opens
      const unread = snap.docs.filter(d => !d.data().read);
      await Promise.all(unread.map(d => updateDoc(doc(db,'notifications',d.id), { read:true })));
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const markAllRead = async () => {
    const unread = notifications.filter(n => !n.read);
    await Promise.all(unread.map(n => updateDoc(doc(db,'notifications',n.id), { read:true })));
    setNotifications(prev => prev.map(n => ({ ...n, read:true })));
  };

  const getIcon = (type) => {
    switch(type) {
      case 'new_bracket':      return '🏆';
      case 'badge_earned':     return '🎖️';
      case 'streak':           return '🔥';
      case 'streak_reminder':  return '⚠️';
      case 'streak_milestone': return '⭐';
      case 'milestone':        return '⭐';
      default:                 return '🔔';
    }
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    const diff  = Date.now() - date.getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins < 1)   return 'just now';
    if (mins < 60)  return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div style={{ position:'fixed', inset:0, zIndex:200, display:'flex', flexDirection:'column', backgroundColor:C.bg, fontFamily:font }}>

      {/* ── Header ── */}
      <div style={{ padding:'14px 18px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', backgroundColor:C.bg }}>
        <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
          <div onClick={onClose} style={{ cursor:'pointer', color:C.textMuted, fontSize:'20px', lineHeight:1 }}>←</div>
          <div style={{ fontSize:'17px', fontWeight:'800', color:C.textPrimary }}>Notifications</div>
          {unreadCount > 0 && (
            <div style={{ backgroundColor:C.orange, borderRadius:'100px', padding:'2px 8px', fontSize:'11px', fontWeight:'700', color:'#fff' }}>{unreadCount}</div>
          )}
        </div>
        {unreadCount > 0 && (
          <div onClick={markAllRead} style={{ fontSize:'12px', color:C.orange, fontWeight:'700', cursor:'pointer' }}>Mark all read</div>
        )}
      </div>

      <div style={{ flex:1, overflowY:'auto', padding:'12px 16px' }}>

        {/* ── Push permission banner ── */}
        {showPushBanner && pushStatus !== 'granted' && pushStatus !== 'denied' && (
          <div style={{ backgroundColor:'rgba(255,107,0,0.08)', border:`1px solid ${C.orangeBorder}`, borderRadius:'14px', padding:'16px', marginBottom:'16px' }}>
            <div style={{ display:'flex', gap:'12px', alignItems:'flex-start' }}>
              <span style={{ fontSize:'28px', flexShrink:0 }}>🔔</span>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:'14px', fontWeight:'700', color:C.textPrimary, marginBottom:'4px' }}>
                  Never miss your streak
                </div>
                <div style={{ fontSize:'12px', color:C.textSecondary, lineHeight:'1.5', marginBottom:'12px' }}>
                  Get a reminder at 6pm if you haven't played today's bracket yet.
                </div>
                <div style={{ display:'flex', gap:'8px' }}>
                  <button
                    onClick={handleEnablePush}
                    disabled={pushStatus === 'requesting'}
                    style={{ flex:1, backgroundColor:pushStatus==='requesting'?'rgba(255,107,0,0.4)':C.orange, color:'#fff', border:'none', borderRadius:'10px', padding:'10px', fontSize:'12px', fontWeight:'700', fontFamily:font, cursor:pushStatus==='requesting'?'not-allowed':'pointer' }}
                  >
                    {pushStatus === 'requesting' ? 'Enabling...' : '🔔 Enable reminders'}
                  </button>
                  <button
                    onClick={() => setShowPushBanner(false)}
                    style={{ backgroundColor:'transparent', color:C.textMuted, border:`1px solid rgba(255,255,255,0.1)`, borderRadius:'10px', padding:'10px 14px', fontSize:'12px', cursor:'pointer' }}
                  >
                    Not now
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Push denied message ── */}
        {pushStatus === 'denied' && (
          <div style={{ backgroundColor:C.bgCard, border:`1px solid ${C.border}`, borderRadius:'12px', padding:'12px 14px', marginBottom:'16px', display:'flex', gap:'10px', alignItems:'center' }}>
            <span style={{ fontSize:'16px' }}>🔕</span>
            <div style={{ fontSize:'12px', color:C.textMuted, lineHeight:'1.4' }}>
              Push notifications are blocked. Enable them in your browser settings to get streak reminders.
            </div>
          </div>
        )}

        {/* ── Push enabled confirmation ── */}
        {pushStatus === 'granted' && !showPushBanner && (
          <div style={{ backgroundColor:C.bgCard, border:`1px solid rgba(0,200,81,0.25)`, borderRadius:'12px', padding:'12px 14px', marginBottom:'16px', display:'flex', gap:'10px', alignItems:'center' }}>
            <span style={{ fontSize:'16px' }}>✅</span>
            <div style={{ fontSize:'12px', color:C.green, fontWeight:'600' }}>
              Streak reminders on — we'll ping you at 6pm ET if you haven't played.
            </div>
          </div>
        )}

        {/* ── Notification list ── */}
        {loading ? (
          <div style={{ textAlign:'center', padding:'40px', color:C.textMuted }}>Loading...</div>
        ) : notifications.length === 0 ? (
          <div style={{ textAlign:'center', padding:'60px 24px' }}>
            <div style={{ fontSize:'48px', marginBottom:'16px' }}>🔔</div>
            <div style={{ fontSize:'16px', fontWeight:'700', color:C.textPrimary, marginBottom:'8px' }}>No notifications yet</div>
            <div style={{ fontSize:'13px', color:C.textMuted }}>We'll let you know when new brackets drop and when you earn badges.</div>
          </div>
        ) : (
          notifications.map(n => (
            <div key={n.id} style={{ backgroundColor:n.read?C.bgCard:'rgba(255,107,0,0.06)', border:`1px solid ${n.read?C.border:C.orangeBorder}`, borderRadius:'12px', padding:'14px 16px', marginBottom:'8px', display:'flex', gap:'12px', alignItems:'flex-start' }}>
              <div style={{ fontSize:'24px', flexShrink:0, marginTop:'2px' }}>{getIcon(n.type)}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:'14px', fontWeight:n.read?'500':'700', color:C.textPrimary, marginBottom:'3px', lineHeight:'1.3' }}>{n.title}</div>
                <div style={{ fontSize:'12px', color:C.textSecondary, lineHeight:'1.4', marginBottom:'5px' }}>{n.message}</div>
                <div style={{ fontSize:'11px', color:C.textMuted }}>{formatTime(n.createdAt)}</div>
              </div>
              {!n.read && <div style={{ width:'8px', height:'8px', borderRadius:'50%', backgroundColor:C.orange, flexShrink:0, marginTop:'6px' }} />}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
